/*
 * Copyright 2024, Corning Optical Communications
 * All Rights Reserved.
 */

import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import { DeviceDetailsProps } from "../types";
import DasPowerDetectorChart from "./DasPowerDetectorChart";
import { Card, CardContent } from "../../../ui/card";
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from "../../../ui/table";
import {
  createPowerDetectorChartConfig,
  buildPowerDetectorParams,
  PowerDetectorParams,
} from "./dasChartUtils";

const Ru2DeviceDetails: React.FC<DeviceDetailsProps> = ({
  device,
  parsedAttributes,
  statusLabel,
  alarmsOnly = false,
  siteObjectKey,
}) => {

  // Parse alarm data from attributes
  let alarms: any[] = [];
  
  try {
    if (parsedAttributes.alarms) {
      const parsedAlarms = typeof parsedAttributes.alarms === 'string' 
        ? JSON.parse(parsedAttributes.alarms) 
        : parsedAttributes.alarms;
      
      // Convert alarms to array format
      if (Array.isArray(parsedAlarms)) {
        alarms = parsedAlarms;
      } else if (typeof parsedAlarms === 'object' && parsedAlarms !== null) {
        alarms = Object.entries(parsedAlarms).map(([name, alarmData]: [string, any]) => ({
          name: name,
          ...(typeof alarmData === 'object' ? alarmData : {})
        }));
      }
    }
  } catch (error) {
    console.error("RU2 Device - Error parsing alarms:", error);
    alarms = [];
  }

  // Check if device is disabled
  const isDeviceDisabled = 
    statusLabel === "Disabled" ||
    device.status === "DISABLED" ||
    parsedAttributes.enabled === "false" ||
    parsedAttributes.enabled === false ||
    parsedAttributes.enabled === "0" ||
    parsedAttributes.enabled === 0;

  console.log("RU2 Device - isDeviceDisabled:", isDeviceDisabled);
  console.log("RU2 Device - Total alarms:", alarms.length);
  console.log("RU2 Device - Alarms array:", alarms);

  const devSerialNumber = parsedAttributes.devSerialNumber || parsedAttributes.elementSerialNumber || device.serialNumber;
  const addParamAliases = (source: Record<string, any> = {}) => {
    const normalized: Record<string, any> = { ...source };
    Object.entries(source).forEach(([key, value]) => {
      if (!key.endsWith("Param") && normalized[`${key}Param`] === undefined) {
        normalized[`${key}Param`] = value;
      }
    });
    return normalized;
  };
  const ru2CodeToAlarmName: Record<string, string> = {
    "1": "powerInterruptionAlarm",
    "4": "overtemperatureAlarm",
    "6": "opticalLinkAlarm",
    "15": "radioModule1OvertemperatureprotectedAlarm",
    "16": "radioModule2OvertemperatureprotectedAlarm",
    "17": "radioModule3OvertemperatureprotectedAlarm",
    "18": "radioModule4OvertemperatureprotectedAlarm",
    "20": "op1TransceiverFailureAlarm",
    "21": "op2TransceiverFailureAlarm",
    "28": "opSlaveTransceiverFailureAlarm",
    "29": "opMasterTransceiverFailureAlarm",
    "30": "module1DLOutputOverpowerAlarm",
    "31": "module2DLOutputOverpowerAlarm",
    "32": "module3DLOutputOverpowerAlarm",
    "33": "module4DLOutputOverpowerAlarm",
    "34": "module1DLOutputUnderpowerAlarm",
    "35": "module2DLOutputUnderpowerAlarm",
    "36": "module3DLOutputUnderpowerAlarm",
    "37": "module4DLOutputUnderpowerAlarm",
    "38": "external1Alarm",
    "39": "external2Alarm",
    "49": "module1VSWRAlarm",
    "50": "module2VSWRAlarm",
    "51": "radioModule1ALCOverRangeAlarm",
    "52": "radioModule2ALCOverRangeAlarm",
    "53": "radioModule3ALCOverRangeAlarm",
    "54": "radioModule4ALCOverRangeAlarm",
    "87": "opticalPortSwitchAlarm",
    "96": "module1OpenLoadAlarm",
    "97": "module2OpenLoadAlarm",
    "101": "fanFailureAlarm",
    "112": "module5DLOutputOverpowerAlarm",
    "113": "module6DLOutputOverpowerAlarm",
    "114": "module7DLOutputOverpowerAlarm",
    "115": "module8DLOutputOverpowerAlarm",
    "116": "module5DLOutputUnderpowerAlarm",
    "117": "module6DLOutputUnderpowerAlarm",
    "118": "module7DLOutputUnderpowerAlarm",
    "119": "module8DLOutputUnderpowerAlarm",
    "120": "radioModule5ALCOverRangeAlarm",
    "121": "radioModule6ALCOverRangeAlarm",
    "122": "radioModule7ALCOverRangeAlarm",
    "123": "radioModule8ALCOverRangeAlarm",
    "144": "module3VSWRAlarm",
    "145": "module4VSWRAlarm",
    "146": "rfLinkAlarm",
    "159": "module3OpenLoadAlarm",
    "160": "module4OpenLoadAlarm",
  };
  const normalizeRu2AlarmName = (name: string) => {
    const coded = String(name || '').match(/^MA6200-G(?:1_RU2|2_RU3)_(\d+)$/i);
    if (!coded) return name;
    return ru2CodeToAlarmName[coded[1]] || name;
  };
  const normalizedAlarms = useMemo(
    () => alarms.map((alarm: any) => ({ ...alarm, name: normalizeRu2AlarmName(alarm?.name) })),
    [alarms]
  );

  // Extract sections from attributes
  const systemInfo = addParamAliases(parsedAttributes.systemInfo || {});
  const lanConnectivity = addParamAliases(parsedAttributes.lanConnectivity || {});

  // Dialog state for module details
  const [moduleDialogOpen, setModuleDialogOpen] = useState(false);
  const [selectedModule, setSelectedModule] = useState<number | null>(null);

  // State for fullscreen chart inside module dialog
  const [fullscreenChart, setFullscreenChart] = useState<'ul' | 'dl' | null>(null);
  const handleOpenFullscreen = (chartType: 'ul' | 'dl') => setFullscreenChart(chartType);
  const handleCloseFullscreen = () => setFullscreenChart(null);
  
  // Function to handle module card click
  const handleModuleClick = (moduleNum: number) => {
    setSelectedModule(moduleNum);
    setModuleDialogOpen(true);
  };
  
  // Function to close module dialog
  const handleCloseModuleDialog = () => {
    setModuleDialogOpen(false);
    setSelectedModule(null);
  };

  // Parent details drawer uses z-index 1400; keep child dialogs above it.
  const dialogLayerSx = {
    zIndex: 1600,
  };
  
  // Function to filter alarms for a specific module
  const getModuleAlarms = (moduleNum: number) => {
    if (!normalizedAlarms || normalizedAlarms.length === 0) return [];
    const modulePattern = new RegExp(`radioModule${moduleNum}`, 'i');
    return normalizedAlarms.filter((alarm: any) => modulePattern.test(alarm.name));
  };

  // Chart configuration for selected module
  const moduleChartConfig = useMemo(() => {
    if (!selectedModule) return null;

    // Device serial number
    // Use siteObjectKey (MA6200 host serial) for KPI API calls, not the RU component's serial
    const serviceDeviceObjectKey = siteObjectKey || device.servicesDeviceObjectKey || device.serialNumber || '';

    const buildKpiRemoteEquipmentObjectKey = () => {
      const devType = String(parsedAttributes.devType || '').toUpperCase();
      if (devType === 'RU3') {
        const controllerFromObjectKey = String(device.objectKey || '').split('.')[0];
        const controllerSerial =
          siteObjectKey ||
          device.servicesDeviceObjectKey ||
          parsedAttributes.devSerialNumber ||
          controllerFromObjectKey ||
          device.serialNumber;
        const devId = parsedAttributes.devId;
        const routing = parsedAttributes.devRoute;
        if (controllerSerial && devId !== undefined && devId !== null && routing) {
          return `${controllerSerial}.${devId}.${routing}`;
        }
      }
      return device.objectKey || device.serialNumber || '';
    };
    
    // For RU2 device, the remoteEquipmentObjectKey format should match the device structure
    const remoteEquipmentObjectKey = buildKpiRemoteEquipmentObjectKey();
    
    // UL Input Power indicator
    const ulIndicator = `radioModule${selectedModule}UlInputpower`;
    
    const ulParams: PowerDetectorParams = {
      remoteEquipmentObjectKey,
      indicators: [ulIndicator],
      bandNames: ['UL Input Power'],
      bandNumbers: [1],
      yAxisMin: -100,  // RU2 UL min power
      yAxisMax: 50,    // RU2 UL max power
      divisor: 1000,   // Divide raw values by 1000 to convert to dBm
    };

    const ulConfig = createPowerDetectorChartConfig('ul', ulParams);
    const ulChartParams = buildPowerDetectorParams(
      serviceDeviceObjectKey,
      remoteEquipmentObjectKey,
      [ulIndicator]
    );

    // DL Output Power indicator
    const dlIndicator = `radioModule${selectedModule}DlOutputpower`;
    
    const dlParams: PowerDetectorParams = {
      remoteEquipmentObjectKey,
      indicators: [dlIndicator],
      bandNames: ['DL Output Power'],
      bandNumbers: [1],
      yAxisMin: -100,  // RU2 DL min power
      yAxisMax: 50,    // RU2 DL max power
      divisor: 1000,   // Divide raw values by 1000 to convert to dBm
    };

    const dlConfig = createPowerDetectorChartConfig('dl', dlParams);
    const dlChartParams = buildPowerDetectorParams(
      serviceDeviceObjectKey,
      remoteEquipmentObjectKey,
      [dlIndicator]
    );

    return {
      ulConfig,
      ulChartParams,
      dlConfig,
      dlChartParams,
    };
  }, [selectedModule, device.serialNumber, device.objectKey, device.servicesDeviceObjectKey, siteObjectKey, parsedAttributes.devType, parsedAttributes.devId, parsedAttributes.devRoute, parsedAttributes.devSerialNumber]);

  // Helper function to get alarm status color
  const getAlarmStatusColor = (alarms: any[], isDisabled: boolean) => {
    if (isDisabled) {
      return '#9e9e9e'; // Gray when device is disabled
    }

    if (!alarms || alarms.length === 0) {
      return '#4caf50'; // Green = clear / no active alarm
    }

    const allAlarmsDisabled = alarms.every(alarm => {
      const hasNoStat = alarm.alarmStat === undefined || alarm.alarmStat === null;
      const severity = parseInt(alarm.alarmSeverity || '0');
      const isZeroSeverity = severity === 0 || severity === 65535;
      return hasNoStat || (alarm.alarmStat !== undefined && isZeroSeverity);
    });
    
    if (allAlarmsDisabled) {
      return '#9e9e9e'; // Gray when severity indicates disabled/not available
    }

    let hasActiveAlarm = false;
    let mostCriticalSeverity = 0;

    for (const alarm of alarms) {
      const stat = alarm.alarmStat;
      const severity = parseInt(alarm.alarmSeverity || '0');
      
      // Skip if severity is 65535 (not available)
      if (severity === 65535) continue;
      
      // Check if alarm is active (stat = 1 means active)
      if (stat === '1' || stat === 1) {
        hasActiveAlarm = true;
        if (severity > mostCriticalSeverity) {
          mostCriticalSeverity = severity;
        }
      }
    }

    if (!hasActiveAlarm) {
      return '#4caf50'; // Green - clear/no active alarms
    }

    // Map severity to colors
    if (mostCriticalSeverity >= 4) {
      return '#f44336'; // Red - Major/Critical
    } else if (mostCriticalSeverity >= 2) {
      return '#ff9800'; // Orange - Warning/Minor
    } else {
      return '#4caf50'; // Green
    }
  };

  // Calculate module status based on module alarms
  const getModuleStatus = (moduleNumber: number) => {
    const moduleAlarms = normalizedAlarms.filter((alarm: any) => {
      const name = alarm.name || '';
      // Match alarms like module1VSWRAlarm, module1DLOutputOverpowerAlarm, radioModule1..., etc.
      const matchesModule = name.includes(`module${moduleNumber}`) || name.includes(`radioModule${moduleNumber}`);
      return matchesModule;
    });
    console.log(`Module ${moduleNumber} alarms:`, moduleAlarms);
    const color = getAlarmStatusColor(moduleAlarms, isDeviceDisabled);
    console.log(`Module ${moduleNumber} color:`, color);
    return color;
  };

  // Calculate optical port status based on transceiver failure alarms
  const getOpticalPortStatus = (portNumber: number) => {
    const portAlarms = normalizedAlarms.filter((alarm: any) => {
      const name = alarm.name || '';
      // Match op1TransceiverFailureAlarm, op2TransceiverFailureAlarm, etc.
      return name === `op${portNumber}TransceiverFailureAlarm`;
    });
    return getAlarmStatusColor(portAlarms, isDeviceDisabled);
  };

  // Calculate optical master port status based on transceiver failure alarm
  const getOpticalMasterPortStatus = () => {
    const portAlarms = normalizedAlarms.filter((alarm: any) => {
      const name = alarm.name || '';
      return name === 'opMasterTransceiverFailureAlarm';
    });
    return getAlarmStatusColor(portAlarms, isDeviceDisabled);
  };

  // Calculate optical slave port status based on transceiver failure alarm
  const getOpticalSlavePortStatus = () => {
    const portAlarms = normalizedAlarms.filter((alarm: any) => {
      const name = alarm.name || '';
      return name === 'opSlaveTransceiverFailureAlarm';
    });
    return getAlarmStatusColor(portAlarms, isDeviceDisabled);
  };

  // Get optical port RX power (divided by 100 for dBm)
  const getOpticalPortRxPower = (portNumber: number) => {
    const rxKey = `op${portNumber}TransceiverRXpower`;
    const value = lanConnectivity[rxKey];
    if (value === undefined || value === null || value === '' || value === '65535') return 'N/A';
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return 'N/A';
    return `${(numValue / 100).toFixed(2)} dBm`;
  };

  // Get optical port TX power (divided by 100 for dBm)
  const getOpticalPortTxPower = (portNumber: number) => {
    const txKey = `op${portNumber}TransceiverTXpower`;
    const value = lanConnectivity[txKey];
    if (value === undefined || value === null || value === '' || value === '65535') return 'N/A';
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return 'N/A';
    return `${(numValue / 100).toFixed(2)} dBm`;
  };

  // Get optical slave port RX power (divided by 100 for dBm)
  const getOpticalSlavePortRxPower = () => {
    const value = lanConnectivity.opslaveTransceiverRXpower;
    if (value === undefined || value === null || value === '' || value === '65535') return 'N/A';
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return 'N/A';
    return `${(numValue / 100).toFixed(2)} dBm`;
  };

  // Get optical slave port TX power (divided by 100 for dBm)
  const getOpticalSlavePortTxPower = () => {
    const value = lanConnectivity.opslaveTransceiverTXpower;
    if (value === undefined || value === null || value === '' || value === '65535') return 'N/A';
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return 'N/A';
    return `${(numValue / 100).toFixed(2)} dBm`;
  };

  // Get optical master port RX power (divided by 100 for dBm)
  const getOpticalMasterPortRxPower = () => {
    const value = lanConnectivity.opmasterTransceiverRXpower;
    if (value === undefined || value === null || value === '' || value === '65535') return 'N/A';
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return 'N/A';
    return `${(numValue / 100).toFixed(2)} dBm`;
  };

  // Get optical master port TX power (divided by 100 for dBm)
  const getOpticalMasterPortTxPower = () => {
    const value = lanConnectivity.opmasterTransceiverTXpower;
    if (value === undefined || value === null || value === '' || value === '65535') return 'N/A';
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return 'N/A';
    return `${(numValue / 100).toFixed(2)} dBm`;
  };

  // Get module RF signal active status
  const getModuleRfSignalActive = (moduleNumber: number) => {
    const paramKey = `radioModule${moduleNumber}RfSignalActive`;
    const value = systemInfo[paramKey];
    if (value === undefined || value === null || value === '' || value === '65535') return null;
    return value === '1' || value === 1;
  };

  // Get module DL centre frequency (divided by 1000 for MHz)
  const getModuleDlFrequency = (moduleNumber: number) => {
    const paramKey = `radioModule${moduleNumber}DlCentreFrequency`;
    const value = systemInfo[paramKey];
    if (value === undefined || value === null || value === '' || value === '65535') return 'N/A';
    const numValue = parseFloat(value);
    if (isNaN(numValue) || numValue === 0) return 'N/A';
    return `${(numValue / 1000).toFixed(0)} MHz`;
  };

  // Get module UL centre frequency (divided by 1000 for MHz)
  const getModuleUlFrequency = (moduleNumber: number) => {
    const paramKey = `radioModule${moduleNumber}UlCentreFrequency`;
    const value = systemInfo[paramKey];
    if (value === undefined || value === null || value === '' || value === '65535') return 'N/A';
    const numValue = parseFloat(value);
    if (isNaN(numValue) || numValue === 0) return 'N/A';
    return `${(numValue / 1000).toFixed(0)} MHz`;
  };

  // Get module UL attenuation
  const getModuleUlAttenuation = (moduleNumber: number) => {
    const paramKey = `radioModule${moduleNumber}UlAttenuation`;
    const value = systemInfo[paramKey];
    if (value === undefined || value === null || value === '65535') return 'N/A';
    return `${value} dB`;
  };

  // Get module DL attenuation
  const getModuleDlAttenuation = (moduleNumber: number) => {
    const paramKey = `radioModule${moduleNumber}DlAttenuation`;
    const value = systemInfo[paramKey];
    if (value === undefined || value === null || value === '65535') return 'N/A';
    return `${value} dB`;
  };

  // Get module DL input power (divided by 1000)
  const getModuleDlInputPower = (moduleNumber: number) => {
    const paramKey = `radioModule${moduleNumber}DlInputpower`;
    const value = systemInfo[paramKey];
    if (value === undefined || value === null || value === '' || value === '65535') return 'N/A';
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return 'N/A';
    return `${(numValue / 1000).toFixed(2)}`;
  };

  // Get module UL input power (divided by 1000)
  const getModuleUlInputPower = (moduleNumber: number) => {
    const paramKey = `radioModule${moduleNumber}UlInputpower`;
    const value = systemInfo[paramKey];
    if (value === undefined || value === null || value === '' || value === '65535') return 'N/A';
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return 'N/A';
    return `${(numValue / 1000).toFixed(2)}`;
  };

  // Get module DL output power (divided by 1000)
  const getModuleDlOutputPower = (moduleNumber: number) => {
    const paramKey = `radioModule${moduleNumber}DlOutputpower`;
    const value = systemInfo[paramKey];
    if (value === undefined || value === null || value === '' || value === '65535') return 'N/A';
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return 'N/A';
    return `${(numValue / 1000).toFixed(2)}`;
  };

  // Get module UL output power (divided by 1000) - Note: RU typically doesn't have UL output, returning N/A
  const getModuleUlOutputPower = (moduleNumber: number) => {
    const paramKey = `radioModule${moduleNumber}UlOutputpower`;
    const value = systemInfo[paramKey];
    if (value === undefined || value === null || value === '' || value === '65535') return 'N/A';
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return 'N/A';
    return `${(numValue / 1000).toFixed(2)}`;
  };

  // Check if module has centre frequencies configured
  const hasModuleFrequencies = (moduleNumber: number) => {
    const dlFreq = getModuleDlFrequency(moduleNumber);
    const ulFreq = getModuleUlFrequency(moduleNumber);
    return dlFreq !== 'N/A' || ulFreq !== 'N/A';
  };

  // Filter general alarms (those without "module" in the name)
  const generalAlarmNames = [
    'external1Alarm',
    'external2Alarm',
    'powerInterruptionAlarm',
    'opticalLinkAlarm',
    'opticalPortSwitchAlarm',
    'rfLinkAlarm'
  ];

  // Format fan working mode
  const formatFanWorkingMode = (value: string | number | undefined) => {
    if (value === undefined || value === null || value === '' || value === 'N/A') return 'N/A';
    const numValue = typeof value === 'string' ? parseInt(value) : value;
    switch (numValue) {
      case 0:
        return 'Low Noise (0)';
      case 1:
        return 'Normal (1)';
      default:
        return 'N/A';
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Main Content */}
      <Box sx={{ 
        width: '100%',
        overflowX: 'auto',
        backgroundColor: 'var(--background)',
      }}>
        <Box sx={{
          minWidth: '1400px',
          p: 3
        }}>
          <Box>
            {/* Summary Card */}
            <Card className="mb-3">
              <CardContent className="px-4 py-4">
                <p className="text-sm font-semibold leading-none tracking-tight text-foreground mt-0 mb-3">General Information</p>
                <div className="grid grid-cols-3 gap-x-8 gap-y-4 items-start">
                <div>
                  <p className="text-xs font-semibold text-muted-foreground mb-1">Model Number</p>
                  <p className="text-sm text-foreground">{parsedAttributes.devModelNumber || device.model || "N/A"}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-muted-foreground mb-1">Serial Number</p>
                  <p className="text-sm text-foreground">{devSerialNumber || "N/A"}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-muted-foreground mb-1">Software Version</p>
                  <p className="text-sm text-foreground">{parsedAttributes.softwareVersion || "N/A"}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-muted-foreground mb-1">Temperature</p>
                  <p className="text-sm text-foreground">{systemInfo.elementOperatingTemperatureParam ? `${systemInfo.elementOperatingTemperature}\u00b0C` : "N/A"}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-muted-foreground mb-1">Location</p>
                  <p className="text-sm text-foreground">{device.location || parsedAttributes.location || parsedAttributes.devSiteName || "N/A"}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-muted-foreground mb-1">Last Update</p>
                  <p className="text-sm text-foreground">{device.lastSeen || device.updatedAt || "N/A"}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Optical Ports Display - 4 ports: Master, Slave, OP1, OP2 */}
          <Card className="mb-4">
            <CardContent className="px-4 py-4">
              <p className="text-sm font-semibold leading-none tracking-tight text-foreground mt-0 mb-3">Optical Ports</p>
              <Box sx={{
                display: 'flex', 
                gap: 1.5,
                justifyContent: 'center',
                pb: 1,
              }}>
                {/* Master Port */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 0.3,
                    width: 85,
                    flexShrink: 0
                  }}
                >
                  {/* Power readings above port box */}
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-end',
                    width: '100%',
                    mb: 0.3,
                    px: 0.3,
                    gap: 1.5
                  }}>
                    <Box sx={{ 
                      textAlign: 'center',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center'
                    }}>
                      <Typography variant="caption" sx={{ 
                        display: 'block',
                        fontSize: '0.5rem',
                        color: getOpticalMasterPortRxPower() !== 'N/A' ? 'var(--success)' : 'var(--muted-foreground)',
                        fontWeight: 600,
                        mb: 0.2,
                        whiteSpace: 'nowrap'
                      }}>
                        {getOpticalMasterPortRxPower()}
                      </Typography>
                      <Typography sx={{ 
                        fontSize: '0.8rem',
                        color: 'var(--primary)',
                        fontWeight: 700,
                        lineHeight: 1
                      }}>
                        ↓
                      </Typography>
                    </Box>
                    <Box sx={{ 
                      textAlign: 'center',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center'
                    }}>
                      <Typography variant="caption" sx={{ 
                        display: 'block',
                        fontSize: '0.5rem',
                        color: getOpticalMasterPortTxPower() !== 'N/A' ? 'var(--success)' : 'var(--muted-foreground)',
                        fontWeight: 600,
                        mb: 0.2,
                        whiteSpace: 'nowrap'
                      }}>
                        {getOpticalMasterPortTxPower()}
                      </Typography>
                      <Typography sx={{ 
                        fontSize: '0.8rem',
                        color: 'var(--primary)',
                        fontWeight: 700,
                        lineHeight: 1
                      }}>
                        ↑
                      </Typography>
                    </Box>
                  </Box>
                  
                  {/* Port box */}
                  <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 0.5,
                    width: '100%',
                    p: 1.5,
                    border: '2px solid var(--border)',
                    borderRadius: 2,
                    backgroundColor: "var(--card) !important",
                    boxShadow: 2,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4,
                      borderColor: 'var(--primary)',
                      cursor: 'pointer'
                    }
                  }}
                >
                  <Typography variant="subtitle2" sx={{ 
                    fontWeight: 700,
                    color: 'var(--foreground)',
                    fontSize: '0.75rem'
                  }}>
                    Master
                  </Typography>
                  <Box sx={{
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    backgroundColor: getOpticalMasterPortStatus(),
                    border: '2px solid var(--border)',
                    transition: 'all 0.2s ease'
                  }} />
                </Box>
              </Box>

                {/* Slave Port */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 0.3,
                    width: 85,
                    flexShrink: 0
                  }}
                >
                  {/* Power readings above port box */}
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-end',
                    width: '100%',
                    mb: 0.3,
                    px: 0.3,
                    gap: 1.5
                  }}>
                    <Box sx={{ 
                      textAlign: 'center',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center'
                    }}>
                      <Typography variant="caption" sx={{ 
                        display: 'block',
                        fontSize: '0.5rem',
                        color: getOpticalSlavePortRxPower() !== 'N/A' ? 'var(--success)' : 'var(--muted-foreground)',
                        fontWeight: 600,
                        mb: 0.2,
                        whiteSpace: 'nowrap'
                      }}>
                        {getOpticalSlavePortRxPower()}
                      </Typography>
                      <Typography sx={{ 
                        fontSize: '0.8rem',
                        color: 'var(--primary)',
                        fontWeight: 700,
                        lineHeight: 1
                      }}>
                        ↓
                      </Typography>
                    </Box>
                    <Box sx={{ 
                      textAlign: 'center',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center'
                    }}>
                      <Typography variant="caption" sx={{ 
                        display: 'block',
                        fontSize: '0.5rem',
                        color: getOpticalSlavePortTxPower() !== 'N/A' ? 'var(--success)' : 'var(--muted-foreground)',
                        fontWeight: 600,
                        mb: 0.2,
                        whiteSpace: 'nowrap'
                      }}>
                        {getOpticalSlavePortTxPower()}
                      </Typography>
                      <Typography sx={{ 
                        fontSize: '0.8rem',
                        color: 'var(--primary)',
                        fontWeight: 700,
                        lineHeight: 1
                      }}>
                        ↑
                      </Typography>
                    </Box>
                  </Box>
                  
                  {/* Port box */}
                  <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 0.5,
                    width: '100%',
                    p: 1.5,
                    border: '2px solid var(--border)',
                    borderRadius: 2,
                    backgroundColor: "var(--card) !important",
                    boxShadow: 2,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4,
                      borderColor: 'var(--primary)',
                      cursor: 'pointer'
                    }
                  }}
                >
                  <Typography variant="subtitle2" sx={{ 
                    fontWeight: 700,
                    color: 'var(--foreground)',
                    fontSize: '0.75rem'
                  }}>
                    Slave
                  </Typography>
                  <Box sx={{
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    backgroundColor: getOpticalSlavePortStatus(),
                    border: '2px solid var(--border)',
                    transition: 'all 0.2s ease'
                  }} />
                </Box>
              </Box>

                {/* Spacer */}
                <Box sx={{ width: 20 }} />

                {/* OP1 and OP2 Ports */}
                {[1, 2].map((portNum) => (
                <Box
                  key={portNum}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 0.3,
                    width: 85,
                    flexShrink: 0
                  }}
                >
                  {/* Power readings above port box */}
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-end',
                    width: '100%',
                    mb: 0.3,
                    px: 0.3,
                    gap: 1.5
                  }}>
                    <Box sx={{ 
                      textAlign: 'center',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center'
                    }}>
                      <Typography variant="caption" sx={{ 
                        display: 'block',
                        fontSize: '0.5rem',
                        color: getOpticalPortRxPower(portNum) !== 'N/A' ? 'var(--success)' : 'var(--muted-foreground)',
                        fontWeight: 600,
                        mb: 0.2,
                        whiteSpace: 'nowrap'
                      }}>
                        {getOpticalPortRxPower(portNum)}
                      </Typography>
                      <Typography sx={{ 
                        fontSize: '0.8rem',
                        color: 'var(--primary)',
                        fontWeight: 700,
                        lineHeight: 1
                      }}>
                        ↓
                      </Typography>
                    </Box>
                    <Box sx={{ 
                      textAlign: 'center',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center'
                    }}>
                      <Typography variant="caption" sx={{ 
                        display: 'block',
                        fontSize: '0.5rem',
                        color: getOpticalPortTxPower(portNum) !== 'N/A' ? 'var(--success)' : 'var(--muted-foreground)',
                        fontWeight: 600,
                        mb: 0.2,
                        whiteSpace: 'nowrap'
                      }}>
                        {getOpticalPortTxPower(portNum)}
                      </Typography>
                      <Typography sx={{ 
                        fontSize: '0.8rem',
                        color: 'var(--primary)',
                        fontWeight: 700,
                        lineHeight: 1
                      }}>
                        ↑
                      </Typography>
                    </Box>
                  </Box>
                  
                  {/* Port box */}
                  <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 0.5,
                    width: '100%',
                    p: 1.5,
                    border: '2px solid var(--border)',
                    borderRadius: 2,
                    backgroundColor: "var(--card) !important",
                    boxShadow: 2,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4,
                      borderColor: 'var(--primary)',
                      cursor: 'pointer'
                    }
                  }}
                >
                  <Typography variant="subtitle2" sx={{ 
                    fontWeight: 700,
                    color: 'var(--foreground)',
                    fontSize: '0.75rem'
                  }}>
                    OP {portNum}
                  </Typography>
                  <Box sx={{
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    backgroundColor: getOpticalPortStatus(portNum),
                    border: '2px solid var(--border)',

                    transition: 'all 0.2s ease'
                  }} />
                </Box>
              </Box>
            ))}
            </Box>
            </CardContent>
          </Card>

        <Box sx={{ mb: 6 }}>
          <p className="text-sm font-semibold leading-none tracking-tight text-foreground mt-0 mb-4">
            Radio Modules
          </p>
            
            {/* All 8 Modules in Single Row */}
            <Grid container spacing={1.5} sx={{ flexWrap: 'nowrap' }}>
              {[1, 2, 3, 4, 5, 6, 7, 8].map((moduleNum) => (
                <Grid item sx={{ minWidth: '145px', flex: 1 }} key={moduleNum}>
                  <Card 
                    onClick={() => handleModuleClick(moduleNum)}
                    className={`h-full cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md hover:border-primary ${hasModuleFrequencies(moduleNum) ? "border-2 border-success" : ""}`}
                  >
                    <CardContent className="py-1.5 px-3">
                      {/* Module Header */}
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 0.5,
                        pb: 0.5,
                        borderBottom: '1px solid var(--border)'
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'var(--foreground)', fontSize: '0.8rem' }}>
                            M{moduleNum}
                          </Typography>
                          <Box sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            backgroundColor: getModuleStatus(moduleNum),
                            border: '2px solid var(--border)'
                          }} />
                        </Box>
                        <Typography variant="caption" sx={{ fontSize: '0.55rem', color: 'var(--muted-foreground)' }}>
                          RF: <strong>{getModuleRfSignalActive(moduleNum) === true ? 'ON' : getModuleRfSignalActive(moduleNum) === false ? 'OFF' : 'N/A'}</strong>
                        </Typography>
                      </Box>

                      {/* DL and UL Split Layout */}
                      <Box sx={{ display: 'flex', gap: 0.75 }}>
                        {/* DL Column */}
                        <Box sx={{ flex: 1, borderRight: '1px solid var(--border)', pr: 0.75 }}>
                          <p className="text-xs font-bold text-chart-3 mt-0 mb-0.5">↓ DL</p>
                          <p className={`text-[0.55rem] mt-0 mb-0.5 ${getModuleDlAttenuation(moduleNum) !== 'N/A' ? 'text-success' : 'text-muted-foreground'}`}>
                            Att: <strong>{getModuleDlAttenuation(moduleNum)}</strong>
                          </p>
                          <p className={`text-[0.55rem] mt-0 ${getModuleDlFrequency(moduleNum) !== 'N/A' ? 'text-success' : 'text-muted-foreground'}`}>
                            Freq: <strong>{getModuleDlFrequency(moduleNum)}</strong>
                          </p>
                        </Box>

                        {/* UL Column */}
                        <Box sx={{ flex: 1, pl: 0.75 }}>
                          <p className="text-xs font-bold text-chart-3 mt-0 mb-0.5">↑ UL</p>
                          <p className={`text-[0.55rem] mt-0 mb-0.5 ${getModuleUlAttenuation(moduleNum) !== 'N/A' ? 'text-success' : 'text-muted-foreground'}`}>
                            Att: <strong>{getModuleUlAttenuation(moduleNum)}</strong>
                          </p>
                          <p className={`text-[0.55rem] mt-0 ${getModuleUlFrequency(moduleNum) !== 'N/A' ? 'text-success' : 'text-muted-foreground'}`}>
                            Freq: <strong>{getModuleUlFrequency(moduleNum)}</strong>
                          </p>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
            
            {/* Output Power Display Row Below All Modules */}
            <Box sx={{
              display: 'flex',
              gap: 1.5,
              justifyContent: 'center',
              mt: 1,
              mb: 2
            }}>
              {[1, 2, 3, 4, 5, 6, 7, 8].map((moduleNum) => (
                <Box key={moduleNum} sx={{
                  display: 'flex',
                  justifyContent: 'space-around',
                  minWidth: '145px',
                  flex: 1
                }}>
                  {/* DL Output */}
                  <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 0.2
                  }}>
                    <Typography sx={{
                      fontSize: '1rem',
                      color: 'var(--primary)',
                      fontWeight: 700,
                      lineHeight: 1
                    }}>
                      ↓
                    </Typography>
                    <Typography sx={{
                      fontSize: '0.6rem',
                      color: getModuleDlOutputPower(moduleNum) !== 'N/A' ? 'var(--success)' : 'var(--muted-foreground)',
                      fontWeight: 600
                    }}>
                      {getModuleDlOutputPower(moduleNum)}
                    </Typography>
                  </Box>
                  
                  {/* UL Output */}
                  <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 0.2
                  }}>
                    <Typography sx={{
                      fontSize: '1rem',
                      color: 'var(--primary)',
                      fontWeight: 700,
                      lineHeight: 1
                    }}>
                      ↑
                    </Typography>
                    <Typography sx={{
                      fontSize: '0.6rem',
                      color: getModuleUlInputPower(moduleNum) !== 'N/A' ? 'var(--success)' : 'var(--muted-foreground)',
                      fontWeight: 600
                    }}>
                      {getModuleUlInputPower(moduleNum)}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Alarms Section - Grid Layout */}
          <Grid container spacing={2}>
            {/* General Alarms Section */}
            <Grid item xs={12} md={8}>
              <Card className="h-full">
                <CardContent className="px-4 py-4">
                  <p className="text-sm font-semibold leading-none tracking-tight text-foreground mt-0 mb-3">General Alarms</p>
                  <Grid container spacing={2}>
                    {generalAlarmNames.map((alarmName) => {
                      const alarm = normalizedAlarms.find(a => a.name === alarmName);
                      const statusColor = alarm ? getAlarmStatusColor([alarm], isDeviceDisabled) : '#9e9e9e';
                      // Format alarm name for display (e.g., "external1Alarm" -> "External 1 Alarm")
                      const displayName = alarmName
                        .replace(/([A-Z])/g, ' $1')
                        .replace(/^./, str => str.toUpperCase())
                        .replace(/(\d+)/g, ' $1')
                        .trim();
                      
                      return (
                        <Grid item xs={6} sm={4} md={4} key={alarmName}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                backgroundColor: statusColor,
                                border: '2px solid var(--border)',
                              }}
                            />
                            <p className="text-sm text-foreground">{displayName}</p>
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Fan Info Section */}
            <Grid item xs={12} md={4}>
              <Card className="h-full">
                <CardContent className="px-4 py-4">
                  <p className="text-sm font-semibold leading-none tracking-tight text-foreground mt-0 mb-3">Fan Info</p>
                  <div className="flex flex-row gap-8 items-start">
                    {/* Fan Failure Alarm */}
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground mb-1">Alarm</p>
                      {(() => {
                        const fanAlarm = normalizedAlarms.find(a => a.name === 'fanFailureAlarm');
                        const statusColor = fanAlarm ? getAlarmStatusColor([fanAlarm], isDeviceDisabled) : '#9e9e9e';
                        return (
                          <div className="flex items-center gap-1.5">
                            <Box
                              sx={{
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                backgroundColor: statusColor,
                                border: '2px solid var(--border)'
                              }}
                            />
                            <p className="text-sm text-foreground">Fan Failure</p>
                          </div>
                        );
                      })()}
                    </div>

                    {/* Fan Working Mode */}
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground mb-1">Mode</p>
                      <p className="text-sm text-foreground">{formatFanWorkingMode(systemInfo.fanWorkingMode)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          </Box>
        </Box>
      </Box>
      
      {/* Module Detail Dialog */}
      <Dialog 
        open={moduleDialogOpen} 
        onClose={handleCloseModuleDialog}
        maxWidth="lg"
        fullWidth
        sx={dialogLayerSx}
        PaperProps={{
          sx: {
            backgroundColor: "var(--card) !important",
            border: '1px solid var(--border)',
            borderRadius: '12px',
            minHeight: '80vh',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: '2px solid var(--border)',
          pb: 2,
          px: 4
        }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: 'var(--foreground)' }}>
            Module {selectedModule} Details
          </Typography>
          <IconButton onClick={handleCloseModuleDialog} size="small" sx={{ color: 'var(--foreground)' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 4, px: 4, pb: 4 }}>
          {selectedModule && (
            <Box sx={{ maxWidth: '900px', mx: 'auto', mt: 3 }}>
              {/* Module Card */}
              <Card className={`mb-3 ${hasModuleFrequencies(selectedModule) ? "border-2 border-success" : ""}`}>
                <CardContent className="py-3 px-4">
                  {/* Module Header */}
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 1.15,
                    pb: 1.15,
                    borderBottom: '1px solid var(--border)'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.15 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'var(--foreground)', fontSize: '1rem' }}>
                        Module {selectedModule}
                      </Typography>
                      <Box sx={{
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        backgroundColor: getModuleStatus(selectedModule),
                        border: '2px solid var(--border)'
                      }} />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ color: 'var(--muted-foreground)', fontSize: '0.75rem' }}>
                        RF Signal: <strong>{getModuleRfSignalActive(selectedModule)}</strong>
                      </Typography>
                    </Box>
                  </Box>

                  {/* DL and UL Split Layout */}
                  <Grid container spacing={1.5}>
                    {/* DL Column */}
                    <Grid item xs={6}>
                      <Box sx={{ 
                        p: 1.5, 
                        borderRight: '1px solid var(--border)',
                        backgroundColor: "var(--card) !important",
                        borderRadius: 0.75
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.15 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'var(--chart-3)', fontSize: '0.85rem' }}>
                            ↓ Downlink (DL)
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Box>
                            <Typography variant="caption" sx={{ color: 'var(--muted-foreground)', display: 'block', mb: 0.3, fontSize: '0.65rem' }}>
                              Attenuation
                            </Typography>
                            <Typography variant="caption" sx={{ 
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              color: getModuleDlAttenuation(selectedModule) !== 'N/A' ? 'var(--success)' : 'var(--muted-foreground)'
                            }}>
                              {getModuleDlAttenuation(selectedModule)}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" sx={{ color: 'var(--muted-foreground)', display: 'block', mb: 0.3, fontSize: '0.65rem' }}>
                              Frequency
                            </Typography>
                            <Typography variant="caption" sx={{ 
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              color: getModuleDlFrequency(selectedModule) !== 'N/A' ? 'var(--success)' : 'var(--muted-foreground)'
                            }}>
                              {getModuleDlFrequency(selectedModule)}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Grid>

                    {/* UL Column */}
                    <Grid item xs={6}>
                      <Box sx={{ 
                        p: 1.5,
                        backgroundColor: "var(--card) !important",
                        borderRadius: 0.75
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.15 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'var(--chart-3)', fontSize: '0.85rem' }}>
                            ↑ Uplink (UL)
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Box>
                            <Typography variant="caption" sx={{ color: 'var(--muted-foreground)', display: 'block', mb: 0.3, fontSize: '0.65rem' }}>
                              Attenuation
                            </Typography>
                            <Typography variant="caption" sx={{ 
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              color: getModuleUlAttenuation(selectedModule) !== 'N/A' ? 'var(--success)' : 'var(--muted-foreground)'
                            }}>
                              {getModuleUlAttenuation(selectedModule)}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" sx={{ color: 'var(--muted-foreground)', display: 'block', mb: 0.3, fontSize: '0.65rem' }}>
                              Frequency
                            </Typography>
                            <Typography variant="caption" sx={{ 
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              color: getModuleUlFrequency(selectedModule) !== 'N/A' ? 'var(--success)' : 'var(--muted-foreground)'
                            }}>
                              {getModuleUlFrequency(selectedModule)}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* DL Output Power and UL Input Power below the module card */}
              <Grid container spacing={1.5} sx={{ mb: 2 }}>
                {/* DL Output Power */}
                <Grid item xs={6}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 0.5 }}>
                    <Typography sx={{ fontSize: '1.1rem', color: 'var(--primary)', fontWeight: 700, lineHeight: 1 }}>
                      ↓
                    </Typography>
                    <Typography sx={{
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      color: getModuleDlOutputPower(selectedModule) !== 'N/A' ? 'var(--success)' : 'var(--muted-foreground)'
                    }}>
                      {getModuleDlOutputPower(selectedModule)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'var(--muted-foreground)', fontSize: '0.65rem' }}>
                      DL Output Power
                    </Typography>
                  </Box>
                </Grid>
                {/* UL Input Power */}
                <Grid item xs={6}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 0.5 }}>
                    <Typography sx={{ fontSize: '1.1rem', color: 'var(--primary)', fontWeight: 700, lineHeight: 1 }}>
                      ↑
                    </Typography>
                    <Typography sx={{
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      color: getModuleUlInputPower(selectedModule) !== 'N/A' ? 'var(--success)' : 'var(--muted-foreground)'
                    }}>
                      {getModuleUlInputPower(selectedModule)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'var(--muted-foreground)', fontSize: '0.65rem' }}>
                      UL Input Power
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              {/* UL Input Power Chart */}
              {moduleChartConfig && (
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ width: '100%' }}>
                    <Box sx={{ minHeight: 300 }}>
                      <DasPowerDetectorChart
                        config={{
                          ...moduleChartConfig.ulConfig,
                          title: 'UL Input Power',
                        }}
                        onFullscreenToggle={() => handleOpenFullscreen('ul')}
                        isFullscreen={false}
                        additionalParams={moduleChartConfig.ulChartParams}
                      />
                    </Box>
                  </Box>
                </Box>
              )}

              {/* DL Output Power Chart */}
              {moduleChartConfig && (
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ width: '100%' }}>
                    <Box sx={{ minHeight: 300 }}>
                      <DasPowerDetectorChart
                        config={{
                          ...moduleChartConfig.dlConfig,
                          title: 'DL Output Power',
                        }}
                        onFullscreenToggle={() => handleOpenFullscreen('dl')}
                        isFullscreen={false}
                        additionalParams={moduleChartConfig.dlChartParams}
                      />
                    </Box>
                  </Box>
                </Box>
              )}

              {/* Module Alarms */}
              <Card className="mt-2">
                <CardContent className="px-4 py-4">
                  <p className="text-sm font-semibold leading-none tracking-tight text-foreground mt-0 mb-3">
                    Module {selectedModule} Alarms
                  </p>
                
                  {(() => {
                    // Get all alarms for the selected module
                    const moduleAlarms = normalizedAlarms.filter((alarm: any) => {
                      const name = alarm.name || '';
                      // Filter out OpenLoadAlarm alarms
                      if (name.includes('OpenLoadAlarm')) {
                        return false;
                      }
                      // Match alarms like module1VSWRAlarm, module1DLOutputOverpowerAlarm, radioModule1..., etc.
                      return name.includes(`module${selectedModule}`) || name.includes(`radioModule${selectedModule}`);
                    });

                    if (moduleAlarms.length === 0) {
                      return (
                        <div className="p-3 text-center border border-border rounded">
                          <p className="text-sm text-muted-foreground mt-0">No module-specific alarms configured</p>
                        </div>
                      );
                    }

                    return (
                      <div className="border border-border rounded overflow-hidden">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="font-semibold min-w-[200px]">Alarm Name</TableHead>
                              <TableHead className="font-semibold text-center w-[150px]">Status</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {moduleAlarms.map((alarm: any, idx: number) => {
                              const severity = parseInt(alarm.alarmSeverity || '0');
                              const stat = alarm.alarmStat;
                              const isActive = stat === '1' || stat === 1;
                              const isDisabled = severity === 0 || severity === 65535;
                              
                              // Calculate status color class
                              let dotClass = 'bg-muted-foreground';
                              if (!isDeviceDisabled && !isDisabled && isActive) {
                                if (severity >= 4) {
                                  dotClass = 'bg-destructive';
                                } else if (severity >= 2) {
                                  dotClass = 'bg-warning';
                                } else {
                                  dotClass = 'bg-success';
                                }
                              } else if (!isDeviceDisabled && !isDisabled && !isActive) {
                                dotClass = 'bg-success';
                              }

                              // Format alarm name for display
                              const displayName = alarm.name
                                .replace(/radioModule\d+/, '')
                                .replace(/module\d+/, '')
                                .replace(/([A-Z])/g, ' $1')
                                .replace(/^./, (str: string) => str.toUpperCase())
                                .trim();

                              return (
                                <TableRow key={`${alarm.name}-${idx}`}>
                                  <TableCell>
                                    <p className="text-xs font-medium text-foreground mt-0">{displayName}</p>
                                  </TableCell>
                                  <TableCell className="text-center">
                                    <div className="flex items-center justify-center gap-1.5">
                                      <div className={`w-2 h-2 rounded-full ${dotClass}`} />
                                      {alarm.threshold && (
                                        <span className="text-[0.7rem] font-medium text-foreground">{alarm.threshold}</span>
                                      )}
                                    </div>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* UL Input Power Fullscreen Dialog */}
      <Dialog
        open={fullscreenChart === 'ul'}
        onClose={handleCloseFullscreen}
        maxWidth="xl"
        fullWidth
        sx={dialogLayerSx}
        PaperProps={{
          sx: {
            backgroundColor: "var(--card) !important",
            border: '1px solid var(--border)',
            borderRadius: '12px',
            minHeight: '90vh',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '2px solid var(--border)',
          pb: 2,
          px: 4
        }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: 'var(--foreground)' }}>
            UL Input Power
          </Typography>
          <IconButton onClick={handleCloseFullscreen} size="small" sx={{ color: 'var(--foreground)' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, px: 3, pb: 3 }}>
          {fullscreenChart === 'ul' && moduleChartConfig && (
            <DasPowerDetectorChart
              config={{
                ...moduleChartConfig.ulConfig,
                title: 'UL Input Power',
              }}
              onFullscreenToggle={handleCloseFullscreen}
              isFullscreen={true}
              additionalParams={moduleChartConfig.ulChartParams}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* DL Output Power Fullscreen Dialog */}
      <Dialog
        open={fullscreenChart === 'dl'}
        onClose={handleCloseFullscreen}
        maxWidth="xl"
        fullWidth
        sx={dialogLayerSx}
        PaperProps={{
          sx: {
            backgroundColor: "var(--card) !important",
            border: '1px solid var(--border)',
            borderRadius: '12px',
            minHeight: '90vh',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '2px solid var(--border)',
          pb: 2,
          px: 4
        }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: 'var(--foreground)' }}>
            DL Output Power
          </Typography>
          <IconButton onClick={handleCloseFullscreen} size="small" sx={{ color: 'var(--foreground)' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, px: 3, pb: 3 }}>
          {fullscreenChart === 'dl' && moduleChartConfig && (
            <DasPowerDetectorChart
              config={{
                ...moduleChartConfig.dlConfig,
                title: 'DL Output Power',
              }}
              onFullscreenToggle={handleCloseFullscreen}
              isFullscreen={true}
              additionalParams={moduleChartConfig.dlChartParams}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Ru2DeviceDetails;
